import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  FormHelperText,
} from '@mui/material';
import dayjs from 'dayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FormSectionHeader from '../common/FormSectionHeader';
import { isCheckInTimeLate, getAttendanceStatusForCheckIn } from '../../utils/attendanceValidation';
import FormEmployeeSearchField from '../common/FormEmployeeSearchField';

const STATUS_CHOICES = [
  { value: 'Present', label: 'Present' },
  { value: 'Absent', label: 'Absent' },
  { value: 'On Leave', label: 'On Leave' },
  { value: 'Remote Work', label: 'Remote Work' },
  { value: 'Late', label: 'Late' },
  { value: 'Half Day', label: 'Half Day' },
];

const AttendanceForm = ({ open, mode, formData, formErrors, onClose, onSubmit, onInputChange, initialEmployee }) => {
  useEffect(() => {
    if (formData.check_in_time && formData.check_out_time) {
      try {
        const [checkInHour, checkInMin] = formData.check_in_time.split(':').map(Number);
        const [checkOutHour, checkOutMin] = formData.check_out_time.split(':').map(Number);

        const checkInMinutes = checkInHour * 60 + checkInMin;
        let checkOutMinutes = checkOutHour * 60 + checkOutMin;

        if (checkOutMinutes <= checkInMinutes) {
          checkOutMinutes += 24 * 60;
        }

        const totalMinutes = checkOutMinutes - checkInMinutes;
        const totalHours = (totalMinutes / 60).toFixed(2);

        onInputChange('total_hours', totalHours);
      } catch (error) {
        console.error('Error calculating total hours:', error);
      }
    }
  }, [formData.check_in_time, formData.check_out_time, onInputChange]);

  useEffect(() => {
    if (formData.check_in_time && (formData.status === 'Present' || formData.status === 'Late')) {
      const newStatus = getAttendanceStatusForCheckIn(formData.check_in_time, formData.status);
      if (newStatus !== formData.status) {
        onInputChange('status', newStatus);
      }
    }
  }, [formData.check_in_time, formData.status, onInputChange]);

  const getDialogTitle = () => {
    switch (mode) {
      case 'add':
        return 'Add Attendance Record';
      case 'edit':
        return 'Edit Attendance Record';
      case 'view':
        return 'Attendance Details';
      default:
        return 'Attendance Record';
    }
  };

  const isViewMode = mode === 'view';

  const getDialogSubtitle = () => {
    if (isViewMode) return 'Review recorded attendance for this employee and date.';
    if (mode === 'add')
      return 'Select the employee and date, then enter times or choose a status such as Absent or On Leave.';
    return 'Adjust times or status; total hours recalculate when check-in and check-out change.';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, display: 'block' }}>
          {getDialogTitle()}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 400, lineHeight: 1.65 }}>
          {getDialogSubtitle()}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 0.5 }}>
          {!isViewMode && (
            <Alert severity="info" variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              Check-in after <strong>7:15 PM</strong> is flagged as Late automatically. Leave check-out empty for open
              shifts.
            </Alert>
          )}

          <FormSectionHeader
            title="Who and when"
            description="Pick the employee and the calendar date this record applies to."
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <FormEmployeeSearchField
                value={formData.employee}
                onChange={(id) => onInputChange('employee', id === '' ? '' : id.toString())}
                label="Employee"
                required
                disabled={isViewMode}
                error={!!formErrors.employee}
                helperText={formErrors.employee || 'Search by name or employee ID.'}
                initialEmployee={initialEmployee}
                apiFilters={mode === 'add' ? { status: 'Active' } : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date"
                  value={formData.date ? dayjs(formData.date) : null}
                  onChange={(newValue) => {
                    const formatted = newValue ? dayjs(newValue).format('YYYY-MM-DD') : '';
                    onInputChange('date', formatted);
                  }}
                  disabled={isViewMode}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      InputLabelProps: { shrink: true },
                      error: !!formErrors.date,
                      helperText: formErrors.date || 'Workday this attendance entry is for.',
                      required: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <FormSectionHeader
            title="Time and status"
            description="Enter clock times for Present/Late days, or set status only for Absent, On Leave, or Remote Work."
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Check In Time"
                type="time"
                value={formData.check_in_time}
                onChange={(e) => onInputChange('check_in_time', e.target.value)}
                disabled={isViewMode}
                error={!!formErrors.check_in_time}
                helperText={
                  formErrors.check_in_time ||
                  (isCheckInTimeLate(formData.check_in_time)
                    ? 'Will be marked as Late (after 7:15 PM)'
                    : formData.status === 'Late' && !isCheckInTimeLate(formData.check_in_time)
                    ? 'Will be marked as Present (at or before 7:15 PM)'
                    : '24-hour format; used with status Present or Late.')
                }
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiFormHelperText-root': {
                    color: formErrors.check_in_time
                      ? 'error.main'
                      : isCheckInTimeLate(formData.check_in_time) ||
                        (formData.status === 'Late' && !isCheckInTimeLate(formData.check_in_time))
                      ? 'warning.main'
                      : 'text.secondary',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Check Out Time"
                type="time"
                value={formData.check_out_time}
                onChange={(e) => onInputChange('check_out_time', e.target.value)}
                disabled={isViewMode}
                error={!!formErrors.check_out_time}
                helperText={formErrors.check_out_time || 'Optional until the employee clocks out.'}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Hours"
                value={formData.total_hours}
                disabled
                helperText="Calculated automatically based on check-in and check-out times"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.status}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => onInputChange('status', e.target.value)}
                  disabled={isViewMode}
                >
                  {STATUS_CHOICES.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </Select>
                {(formErrors.status || !isViewMode) && (
                  <FormHelperText error={!!formErrors.status}>
                    {formErrors.status || 'Present and Late use check-in time; other statuses may omit times.'}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          <FormSectionHeader
            title="Notes"
            description="Optional context for managers or payroll (e.g. approved late arrival)."
          />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={3}
                value={formData.remarks || ''}
                onChange={(e) => onInputChange('remarks', e.target.value)}
                disabled={isViewMode}
                error={!!formErrors.remarks}
                helperText={formErrors.remarks || 'Visible on attendance reports; not required.'}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!isViewMode && (
          <Button onClick={onSubmit} variant="contained">
            {mode === 'add' ? 'Add Record' : 'Update Record'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

AttendanceForm.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['add', 'edit', 'view']).isRequired,
  formData: PropTypes.shape({
    employee: PropTypes.string,
    date: PropTypes.string,
    check_in_time: PropTypes.string,
    check_out_time: PropTypes.string,
    total_hours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    remarks: PropTypes.string,
  }).isRequired,
  formErrors: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  initialEmployee: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    full_name: PropTypes.string,
    employee_id: PropTypes.string,
  }),
};

AttendanceForm.defaultProps = {
  formErrors: {},
  initialEmployee: undefined,
};

export default AttendanceForm;
